import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { InvoiceDetailsPageContext } from "@insite/content-library/Pages/InvoiceDetailsPage";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { useContext } from "react";
import { css } from "styled-components";

export interface InvoiceDetailsShipmentPackagesStyles {
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    shipmentDateHeader?: DataTableHeaderProps;
    carrierHeader?: DataTableHeaderProps;
    shipViaHeader?: DataTableHeaderProps;
    trackingNumberHeader?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    shipmentDateCell?: DataTableCellProps;
    carrierCell?: DataTableCellProps;
    shipViaCell?: DataTableCellProps;
    trackingNumberCell?: DataTableCellProps;
}

export const shipmentPackagesStyles: InvoiceDetailsShipmentPackagesStyles = {
    tableWrapper: {
        css: css`
            margin: 15px 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            overflow-y: auto;
                        `,
                        css`
                            overflow-y: auto;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = shipmentPackagesStyles;

const InvoiceDetailsShipmentPackages: React.FC = () => {
    const { value: order } = useContext(InvoiceStateContext);
    if (!order || !order.properties.shipments) {
        return null;
    }
    const shipmentObj = JSON.parse(order.properties.shipments);
    if (!shipmentObj || shipmentObj.ShipmentPackages.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.tableWrapper}>
            <DataTable {...styles.dataTable}>
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader {...styles.shipmentDateHeader}>{translate("Ship Date")}</DataTableHeader>
                    <DataTableHeader {...styles.carrierHeader}>{translate("Carrier")}</DataTableHeader>
                    <DataTableHeader {...styles.shipViaHeader}>{translate("Service")}</DataTableHeader>
                    <DataTableHeader {...styles.trackingNumberHeader}>{translate("Tracking #")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {shipmentObj.ShipmentPackages.map(
                        (shipmentPackage: {
                            Id: string | undefined;
                            ShipmentDate: string | Date;
                            Carrier: string;
                            ShipVia: string;
                            TrackingUrl: string;
                        }) => (
                            <DataTableRow {...styles.dataTableRow} key={shipmentPackage.Id}>
                                <DataTableCell {...styles.shipmentDateCell}>
                                    <LocalizedDateTime
                                        dateTime={new Date(shipmentPackage.ShipmentDate)}
                                        options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                    />
                                </DataTableCell>
                                <DataTableCell {...styles.carrierCell}>{shipmentPackage.Carrier}</DataTableCell>
                                <DataTableCell {...styles.shipViaCell}>{shipmentPackage.ShipVia}</DataTableCell>
                                <DataTableCell {...styles.trackingNumberCell}>
                                    {parse(shipmentPackage.TrackingUrl, parserOptions)}
                                </DataTableCell>
                            </DataTableRow>
                        ),
                    )}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InvoiceDetailsShipmentPackages,
    definition: {
        allowedContexts: [InvoiceDetailsPageContext],
        group: "Invoice History",
    },
};
export default widgetModule;
